"use client"

import { useMemo } from "react"
import {
  ClickmapProvider,
  fetchAdapter,
  type ClickmapAdapter,
} from "react-clickmap"
import {
  PA_EVENTS_ENDPOINT,
  PA_HEADERS,
  PA_PROJECT_ID,
} from "./config"

// Captures click / dead-click / rage-click interactions across the storefront
// and ships them to the Medusa backend for the admin heatmap.
//
// Always enabled: this capture is cookie-free and collects no PII (only the
// per-tab sessionStorage id and coarse device type), so it runs regardless of
// the visitor's cookie-consent choice. sampleRate = 1 → 100% of sessions.
//
// sendBeacon is disabled (preferBeacon: false) so the publishable-key header is
// attached to every batch; keepalive ensures the final batch still flushes on
// page unload.
export default function ClickmapTracker({
  children,
}: {
  children: React.ReactNode
}) {
  const adapter = useMemo<ClickmapAdapter>(
    () =>
      fetchAdapter({
        endpoint: PA_EVENTS_ENDPOINT,
        preferBeacon: false,
        keepalive: true,
        headers: PA_HEADERS,
      }),
    []
  )

  return (
    <ClickmapProvider
      adapter={adapter}
      projectId={PA_PROJECT_ID}
      capture={["click", "dead-click", "rage-click"]}
      sampleRate={1}
      enabled={true}
      // Cookie-free, no-PII capture; not consent-gated.
      respectDoNotTrack={false}
      respectGlobalPrivacyControl={false}
      // Never record interactions on elements explicitly marked sensitive.
      maskSelectors={["[data-sensitive]", "[data-private]"]}
    >
      {children}
    </ClickmapProvider>
  )
}
